import { Pill, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { type Worker } from "@src/appV2/Worker/api/types";
import { Fragment, useMemo } from "react";

import { useCredentialRequirementConfiguration } from "../../../Documents/useCredentialRequirementConfiguration";
import { transformResponseIntoPlacement } from "../../api/useFetchPaginatedPlacements";
import { useGetPendingInterviewPlacements } from "../../api/useGetPendingInterviewPlacements";
import { PendingInterviewCard } from "../PendingInterviewCard/PendingInterviewCard";
import { PlacementsAccordionSectionV3 } from "../PlacementAccordionSection/PlacementsAccordionSectionV3";

interface PendingInterviewsSectionProps {
  placementCandidate: PlacementCandidate;
  worker: Worker;
}

export function PendingInterviewsSection(props: PendingInterviewsSectionProps) {
  const { placementCandidate, worker } = props;

  const credentialRequirementConfiguration = useCredentialRequirementConfiguration();

  const {
    data: pendingPlacementsData,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useGetPendingInterviewPlacements(
    {
      workerId: worker.userId,
      placementCandidateId: placementCandidate.id,
      shouldLogEvent: false,
    },
    {
      enabled: isDefined(worker.userId),
    }
  );

  const pendingPlacements = useMemo(() => {
    if (!pendingPlacementsData) {
      return [];
    }

    return pendingPlacementsData.pages.flatMap((page) =>
      page.data.map((placement) =>
        transformResponseIntoPlacement(placement, page.included, credentialRequirementConfiguration)
      )
    );
  }, [pendingPlacementsData, credentialRequirementConfiguration]);

  const totalCount = pendingPlacements.length;

  // Only render this section if there are pending interviews
  if (!isLoading && totalCount === 0) {
    return null;
  }

  return (
    <PlacementsAccordionSectionV3
      initialExpanded
      title={
        <Stack alignItems="center" direction="row" spacing={2}>
          <Text variant="h5">Pending Interviews</Text>
          {totalCount > 0 ? (
            <Pill
              label={totalCount}
              size="small"
              sx={{
                backgroundColor: (theme) => theme.palette.intent?.warning.background,
                color: (theme) => theme.palette.intent?.warning.text,
                padding: 0,
                minHeight: 20,
                minWidth: 20,
                height: "max-content",
                borderRadius: 10,
              }}
            />
          ) : undefined}
        </Stack>
      }
      isLoading={isLoading}
      hasNextPage={hasNextPage ?? false}
      isFetchingNextPage={isFetchingNextPage}
      emptyText={
        <Text variant="body1" sx={{ textAlign: "center" }}>
          You have no pending interviews at this time.
        </Text>
      }
      totalPlacementsCount={totalCount}
      placementIds={pendingPlacements.map((placement) => placement.id)}
      renderPlacementCard={(placementId, index) => {
        const placement = pendingPlacements.find(
          (pendingPlacement) => pendingPlacement.id === placementId
        );

        if (!isDefined(placement)) {
          return null;
        }

        return (
          <Fragment key={placement.id}>
            <PendingInterviewCard
              placement={placement}
              placementCandidate={placementCandidate}
              workerTimezone={worker.tmz}
              index={index}
            />
          </Fragment>
        );
      }}
      onLoadMore={() => {
        void fetchNextPage();
      }}
    />
  );
}
